import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { AnalyticsResourceType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import { shouldShowGraphHeader } from "../../../api/formatters/dashboard";
import { IAppState } from "../../../store/types/store";
import {
  BarMetricData,
  HistogramMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../../types/analytics";
import { ExperienceFe } from "../../../types/schemas/experiences";
import { BarGraph } from "../Graphs/BarGraph";
import { BiaxialBarGraph } from "../Graphs/BiaxialBarGraph";
import { HistogramGraph } from "../Graphs/HistogramGraph";
import { LineGraph } from "../Graphs/LineGraph";
import { StackedAreaGraph } from "../Graphs/StackedAreaGraph";
import { StackedBarGraph } from "../Graphs/StackedBarGraph";
import { GraphConfig, GraphType } from "../Graphs/Utils/graphConfigs";
import { NoGraphData } from "../Graphs/Utils/NoGraphData";
import { ProfitGraphPlaceholder } from "../Graphs/Utils/ProfitGraphPlaceholder";

import { hasFiltersBeenModified } from "./utils";

interface GraphRenderProps {
  config: GraphConfig;
  resource?: ExperienceFe;
  resourceType: AnalyticsResourceType;
  data:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[]
    | StackedAreaMetricData[];
  metricFilterIndex?: number;
  filterMethod?: string;
  aovFilterChoice?: string;
}
export const GraphRender = ({
  config,
  resource,
  resourceType,
  metricFilterIndex,
  data,
  filterMethod,
  aovFilterChoice,
}: GraphRenderProps) => {
  const shouldShowProfit = useConfiguration<boolean>(
    "should_show_profit_in_dashboard"
  );
  const shouldShowProfitPlaceholder = shouldShowGraphHeader(
    shouldShowProfit!.value!,
    config
  );
  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );

  const areFiltersModified = useMemo(() => {
    return hasFiltersBeenModified(dashboardFilters);
  }, [dashboardFilters]);

  return (
    <>
      {shouldShowProfitPlaceholder ? (
        <ProfitGraphPlaceholder />
      ) : data.length < 1 && config?.isLossMetric ? (
        <NoGraphData
          title={"Insufficient Order Count"}
          body={"Check back when more orders have come through this experiment"}
        />
      ) : data.length < 1 ? (
        <NoGraphData
          title={areFiltersModified ? "No Data!" : undefined}
          body={
            areFiltersModified
              ? "Your filters may have resulted in no data. Try resetting them!"
              : undefined
          }
        />
      ) : data ? (
        <Grid item>
          {config.graphType === GraphType.StackedBar ||
          config.graphType === GraphType.ClusteredBar ? (
            <StackedBarGraph
              config={config}
              groupMetricData={data as StackedBarMetricData[]}
            />
          ) : config.graphType === GraphType.Histogram ? (
            <HistogramGraph
              config={config}
              groupMetricData={data as HistogramMetricData[]}
              aovFilterChoice={aovFilterChoice || "all"}
            />
          ) : config.graphType === GraphType.StackedArea ? (
            <StackedAreaGraph
              resource={resource}
              resourceType={resourceType}
              config={config}
              groupMetricData={data as StackedAreaMetricData[]}
            />
          ) : config.graphType === GraphType.Line ? (
            <LineGraph
              resourceType={resourceType}
              resource={resource}
              config={config}
              groupMetricData={data as StackedBarMetricData[]}
              filterMethod={filterMethod}
              metricFilterIndex={metricFilterIndex}
            />
          ) : config.graphType === GraphType.BiaxialBar ? (
            <BiaxialBarGraph
              config={config}
              groupMetricData={data as StackedBarMetricData[]}
            />
          ) : resource ? (
            <BarGraph
              config={config}
              groupMetricData={data as BarMetricData[]}
              filterMethod={filterMethod}
              compareGroupA={dashboardFilters.compareGroupA}
              compareGroupB={dashboardFilters.compareGroupB}
              resource={resource}
              resourceType={resourceType}
            />
          ) : null}
        </Grid>
      ) : null}
    </>
  );
};
